import './SdoAdminPilotRequestsDetails.scss'
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import { GridColDef, GridSortModel, DataGridPro } from '@mui/x-data-grid-pro';
import CurrentRequest from './CurrentRequest';
import Loader from '../Loader/Loader';
import PilotHeader from './PilotHeader';
import SdoRequestStatus from '../SdoRequestStatus/SdoRequestStatus';
import { FormattedSdoAdminRequestHistory, SdoAdminRequestHistory } from '../../models/SdoInterface';
import { formatDatesRequested, formatDatesRequestedOn, getCreatedOn, requestStatusType, sortDateComparator, sortRequestedOnComparator, sortStatusComparator } from '../../pages/SdoAdmin/sdoAdminHelper';
import { useSdoAdminEligibilityCriteriaProvider } from '../../providers/SdoAdminEligibilityCriteriaProvider';
import { useHistory } from "react-router-dom";

const getPosition = (sdoReqListObject) => {
    if(sdoReqListObject?.position){
        switch(sdoReqListObject.position){
            case 'All' : return '';
            default : return `${sdoReqListObject.base}|${sdoReqListObject.equipment}|${sdoReqListObject.position}`;
        }
    }else{
        return '';
    }
};

export default function SdoAdminPilotRequestsDetails({managedPilotId, setManagedPilotId, goBack, selectedPosition}) {
    const sdoAdminEligibilityCriteriaContext = useSdoAdminEligibilityCriteriaProvider();
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState<number>(25);
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'requestedOn', sort: 'desc'}]);
    const [rows, setRows] = useState<Array<FormattedSdoAdminRequestHistory>>([]);
    const [currentRequests, setCurrentRequests] = useState([] as SdoAdminRequestHistory[]);
    const [exceptionButtonProps, setExceptionButtonProps] = useState({
        showCancel: false,
        disabled: false,
        showMaxExceptionWarning: false
    });

    const searchMonth = new Date().getMonth() + 1; 
    const currentYear = new Date().getFullYear();
    const searchYear = currentYear - 3;
    let history = useHistory();
    useEffect(() => {
        sdoAdminEligibilityCriteriaContext.getPilotRequestHistory(managedPilotId, searchMonth, searchYear);
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdoAdminEligibilityCriteriaContext.exceptionRevokeResponse])

    useEffect(() => {
        if(!Object.keys(sdoAdminEligibilityCriteriaContext.pilotRequestsData).length) return;
        const futureRequests = sdoAdminEligibilityCriteriaContext.pilotRequestsData.requestHistory.filter(
            data => new Date(data.startDate).getTime() > Date.now() && data.status === requestStatusType.APPROVED
        );
        setCurrentRequests(futureRequests);

        const requestHistoryRows = sdoAdminEligibilityCriteriaContext.pilotRequestsData.requestHistory.map((request,index) => { 
            return({
                id: index,
                year: request.year,
                dates: {
                    days: request.days,
                    endDate: request.endDate,
                    startDate: request.startDate
                },
                requestedOn: {
                    date: request.requestedOn,
                    status: request.status
                },
                status: {
                    label: request.status,
                    remarks: request.remarks,
                },
                admin: request.admin,
                transactionId: request.transactionId
            })
        });

        setRows(requestHistoryRows);

    }, [sdoAdminEligibilityCriteriaContext.pilotRequestsData])

    useEffect(() => {
        if(!rows.length) return; 
        
        const latestRequest = [...rows].sort((a,b) => 
            (a.status === requestStatusType.EXCEPTION ? new Date(b.requestedOn.date).getTime() : new Date(b.dates.startDate).getTime()) - 
            (b.status === requestStatusType.EXCEPTION ? new Date(a.requestedOn.date).getTime() : new Date(a.dates.startDate).getTime())
        )[0];
        const totalUsedSDOsThisYear = rows.filter(row => row.year === currentYear && (row.status.label === requestStatusType.APPROVED || row.status.label === requestStatusType.SUBMITTED || row.status.label === requestStatusType.CONFLICT)).length;
        const exceptionsForThisYear = rows.filter(row => row.year === currentYear && row.status.label === requestStatusType.EXCEPTION);
        setExceptionButtonProps({
            showCancel: latestRequest.status.label === requestStatusType.EXCEPTION,
            disabled: latestRequest.status.label !== requestStatusType.APPROVED || exceptionsForThisYear.length > 1 || latestRequest.year !== currentYear || (totalUsedSDOsThisYear - exceptionsForThisYear.length) !== 2,
            showMaxExceptionWarning: latestRequest.status.label === requestStatusType.APPROVED && exceptionsForThisYear.length > 1
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows])

    const columns: GridColDef[] = [
        {field: 'year', 
         headerName: 'Year', 
         width: 120,
         renderCell: (params: any) => (<Typography className='table__cell--year'>{params.value}</Typography>)
        },

        {field: 'dates', 
         headerName: 'Dates', 
         width: 275, 
         sortComparator: sortDateComparator,
         valueFormatter: (params: any) => params.value.startDate ? formatDatesRequested(new Date(params.value.startDate), new Date(params.value.endDate), params.value.days) : '-'
        },

        {field: 'requestedOn', 
         headerName: 'Requested on', 
         width: 200, 
         sortComparator: sortRequestedOnComparator,
         renderCell: (params: any) => (<p>{params.value.status === 'EXCEPTION' ? '-' : formatDatesRequestedOn(params.value.date)}</p>),
        },

        {field: 'status', 
         headerName: 'Status', 
         width: 350,
         renderCell: (params: any) => (
            <div className='table__cell--status'>
                <SdoRequestStatus status={params.value.label} remarks={params.value.remarks} />
            </div> 
         ),
         sortComparator: sortStatusComparator 
        },      

        {field: 'admin', 
         headerName: 'Administrator', 
         minWidth: 325,
         flex:1,
         renderCell: (params: any) => (<p>{params.value}</p>),
        },
    ]

    const handleGoBackClick = () => {
        if (goBack === 0) { history.push({ pathname: '/sdo-admin-report-details',  state: {position: getPosition(selectedPosition)}}); }
        else {
            setManagedPilotId('');
        }
    }
    
    const handleSortChange = (model: GridSortModel) => {
        /* prevent the infinite loop by confirming model is different than the current sortModel state when setting a sort field on page load, (fixed in subsequent DataGrid versions) */
        if (JSON.stringify(model) !== JSON.stringify(sortModel)) setSortModel(model);
    };

    /**
     * Grant an exception on the latest Approved Request 
     */
    const handleGrantExceptionClick = () => {
        const year = new Date().getFullYear();
        const createdOn = getCreatedOn(); 
        sdoAdminEligibilityCriteriaContext.grantCancelException(managedPilotId, year, createdOn);
    }

    const handleCancelExceptionClick = () => {
        const year = new Date().getFullYear();
        sdoAdminEligibilityCriteriaContext.grantCancelException(managedPilotId, year);
    }

    const handleExceptionBtn = () => {
        if(!currentRequests.length) {
            if(exceptionButtonProps.showCancel) return <Button variant='outlined' className='btn-exception btn-exception--cancel' onClick={()=>handleCancelExceptionClick()}>{t('specialDayOff.adminEligibilityCriteria.requestHistory.cancelBtn')}</Button>;
            return <Button variant='outlined' className='btn-exception' onClick={()=>handleGrantExceptionClick()} disabled={exceptionButtonProps.disabled}>{t('specialDayOff.adminEligibilityCriteria.requestHistory.grantBtn')}</Button>;
        }
        return null;
    }

    const showAllRows = () => {
        if (rows.length > 100) {
          setPageSize(Math.ceil(rows.length / 25) * 25);
        }
        else if (rows.length > 50) {
          setPageSize(100);
        }
        else if (rows.length > 25) {
          setPageSize(50);
        }
        else {
          setPageSize(25);
        }
    }

    return (
        sdoAdminEligibilityCriteriaContext.pilotRequestHistoryLoading || sdoAdminEligibilityCriteriaContext.exceptionRevokeLoading || !rows.length ? <Loader /> :

       <div className='c-sdo-admin-pilot-request-details'>
            <div className='c-back' onClick={() => handleGoBackClick()}>
                <i className='icon-chevron-thin-left back__icon' /> 
                <span className='back__lbl'>{t('specialDayOff.adminEligibilityCriteria.goBack')}</span>
            </div>

            <PilotHeader />
            {currentRequests.length ? (
                    <div className='c-current-requests-container'> 
                    {currentRequests.map((currentRequest, index) => <CurrentRequest key={index} requestNo={index+1} currentRequest={currentRequest} pilotId={managedPilotId}/>)}
                    </div>
                )
                : ''
            }

            <div className='c-request-history'>
                <div className='c-request-history-header'>
                    <span className='request-history-header__title'>{t('specialDayOff.adminEligibilityCriteria.requestHistory.title')}</span>
                    {handleExceptionBtn()}
                </div>
                {exceptionButtonProps.showMaxExceptionWarning && <div className='notification'> <i className="icon-circle-flight"></i>{t('specialDayOff.adminEligibilityCriteria.requestHistory.error')}</div>}
                <div className='c-request-history-table'>
                    <DataGridPro
                        // autoHeight
                        columns={columns}
                        disableColumnMenu
                        //disableExtendRowFullWidth={true}
                        //disableSelectionOnClick
                        // onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        // pageSizeOptions={[25, 50, 100]}
                        // pageSize={pageSize}
                        pagination
                        rows={rows}
                        sortModel={sortModel}
                        onSortModelChange={(model) => handleSortChange(model)} 
                        disableRowSelectionOnClick
                    />
                    {
                        pageSize < rows.length ? 
                        <div className='enabled-view-all'><span onClick={showAllRows}>View All</span></div> :
                        rows.length > 10 ? 
                        <div className='disabled-view-all'><span>View All</span></div>
                        : <div className='disabled-view-all-adjusted'><span>View All</span></div>
                    }
                </div>
            </div>
       </div>
    )
}
